import { useEffect, useState } from "react";
import { Api } from "../api/baseApi";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

const BlogDetails = () => {
  const { i18n } = useTranslation();
  const { slug } = useParams();
  const { pathname } = useLocation();

  const [blogData, setBlog] = useState(null);

  const stripHtml = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const trimText = (value, maxLength) => {
    if (!value) return "";
    const normalized = value.replace(/\s+/g, " ").trim();
    if (normalized.length <= maxLength) return normalized;
    return `${normalized.slice(0, maxLength - 3).trim()}...`;
  };

  useEffect(() => {
    if (!slug) return;

    const fetchBlog = async () => {
      try {
        const res = await Api.get(`/api/${i18n.language}/blog/${slug}`);
        setBlog(res.data);
      } catch (error) {
        console.error("Failed to fetch blog:", error);
      }
    };

    fetchBlog();
  }, [i18n.language, slug]);

  const getImageUrl = (frontFiles) => {
    const assetBaseUrl = (process.env.REACT_APP_PAGE_URL || "").replace(/\/$/, "");

    if (!frontFiles) return "";

    const rawFile =
      typeof frontFiles === "string"
        ? frontFiles
        : Array.isArray(frontFiles)
          ? frontFiles[0]?.file
          : frontFiles.file;

    if (!rawFile) return "";

    const buildEncodedUrl = (value) => {
      const [pathPart, queryPart] = value.split("?");
      const encodedPath = encodeURI(pathPart);
      return queryPart ? `${encodedPath}?${queryPart}` : encodedPath;
    };

    if (/^https?:\/\//i.test(rawFile)) return buildEncodedUrl(rawFile);
    if (!assetBaseUrl) return buildEncodedUrl(rawFile);

    const normalizedFile = rawFile.startsWith("/") ? rawFile.slice(1) : rawFile;
    return buildEncodedUrl(`${assetBaseUrl}/${normalizedFile}`);
  };

  const normalizeUrl = (value) => {
    if (!value) return "";
    return value.endsWith("/") ? value : `${value}/`;
  };

  const siteUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  const fallbackUrl = siteUrl ? `${siteUrl}${pathname}` : pathname;
  const seo = blogData?.metaTags?.[0]?.translate;
  const sharedLink = blogData?.shereedLinks?.[0];
  const sharedSeo = sharedLink?.translate;
  const title = stripHtml(
    sharedSeo?.title || seo?.title || blogData?.blog?.translate?.title || ""
  );
  const description = stripHtml(
    sharedSeo?.description || seo?.description || blogData?.blog?.translate?.description || ""
  );
  const keywords = stripHtml(seo?.content || "");
  const canonicalUrl = normalizeUrl(fallbackUrl);
  const shareUrl = canonicalUrl;
  const shareImage =
    getImageUrl(sharedLink?.front_files) ||
    getImageUrl(blogData?.blog?.front_files) ||
    (siteUrl ? `${siteUrl}/assets/images/logo/logo.png` : "");
  const socialTitle = trimText(title, 60);
  const socialDescription = trimText(description, 240);

  return (
    <>
      <Helmet>
        {title && <title>{title}</title>}
        {socialDescription && <meta name="description" content={socialDescription} />}
        {keywords && <meta name="keywords" content={keywords} />}

        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:locale" content={i18n.language} />
        <meta property="og:type" content="article" />
        {socialTitle && <meta property="og:title" content={socialTitle} />}
        {socialDescription && (
          <meta property="og:description" content={socialDescription} />
        )}
        <meta property="og:url" content={shareUrl} />
        {shareImage && <meta property="og:image" content={shareImage} />}
        {shareImage && <meta property="og:image:secure_url" content={shareImage} />}
        {shareImage && <meta property="og:image:width" content="1200" />}
        {shareImage && <meta property="og:image:height" content="630" />}
        {socialTitle && <meta property="og:image:alt" content={socialTitle} />}

        <meta name="twitter:card" content="summary_large_image" />
        {socialTitle && <meta name="twitter:title" content={socialTitle} />}
        {socialDescription && (
          <meta name="twitter:description" content={socialDescription} />
        )}
        {shareImage && <meta name="twitter:image" content={shareImage} />}
      </Helmet>

      <section className="blog-details padding-y-120 position-relative overflow-hidden">
        <div className="container container-two">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="blog-details-top mb-64">
                <h1 className="blog-details-top__title mb-4 text-capitalize font-40">
                  {blogData?.blog?.translate?.title && (
                    <span
                      dangerouslySetInnerHTML={{ __html: blogData.blog.translate.title }}
                    />
                  )}
                </h1>
                <p className="blog-details-top__desc">
                  {blogData?.blog?.translate?.description && (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: blogData.blog.translate.description,
                      }}
                    />
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="blog-details-content">
                {blogData?.blog?.front_files && (
                  <div className="blog-details-content__thumb mb-32">
                    <img src={getImageUrl(blogData.blog.front_files)} alt="Blog main" />
                  </div>
                )}

                {!!blogData?.blog?.features?.length &&
                  blogData.blog.features.map((feature, index) => (
                    <div className="pt-30" key={index}>
                      <h4 className="blog-details-content__title mb-24">
                        {feature?.translate?.title && (
                          <span
                            dangerouslySetInnerHTML={{ __html: feature.translate.title }}
                          />
                        )}
                      </h4>
                      <p className="blog-details-content__desc mb-40">
                        {feature?.translate?.description && (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: feature.translate.description,
                            }}
                          />
                        )}
                      </p>

                      <div className="row gy-4">
                        {!!feature?.front_files?.length &&
                          feature.front_files.map((item) => (
                            <div className="col-sm-3 col-3" key={item.id}>
                              <div className="blog-details-content__feature-thumb">
                                <img
                                  src={getImageUrl(item)}
                                  alt={feature?.translate?.title || "Feature"}
                                />
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogDetails;
